import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAuth } from "@/lib/route-guards";
import { LoadingProvider } from "@/contexts/loading-context";
import { handleApiError } from "@/lib/error-handler";
import { useEffect, useState, type FormEvent } from "react";
import Marquee from "react-fast-marquee";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Menu, Edit, Home, List, Trophy, Square } from "lucide-react";
import { SlidingNumber } from "@/components/ui/shadcn-io/sliding-number";
import "./kura.$kuraId.start-new-design.css";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useKuraManualStartDetailQuery, useStartManualLotteryMutation, useStartManualParticipantPropertyLotteryMutation } from "@/hooks/use-kura-queries";
import { useSettingsQuery } from "@/hooks/use-settings-queries";
import { Skeleton } from "@/components/ui/skeleton";
import { LoadingModal } from "@/components/ui/loading-modal";
import { formatNumberWithDots, getResponsiveFontSize } from "@/lib/utils";
import { api } from "@/lib/api";
import { KuraStatus } from "@/types";
export const Route = createFileRoute("/kura/$kuraId/manual-start")({
  beforeLoad: requireAuth,
  component: () => (
    <LoadingProvider>
      <ManualKuraStartPage />
    </LoadingProvider>
  ),
});



function ManualKuraStartPage() {
  const { kuraId } = Route.useParams();

  // Fetch kura start details from API
  const {
    data: kuraStartDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useKuraManualStartDetailQuery(kuraId);

  // Manual lottery start mutations
  const startManualLotteryMutation = useStartManualLotteryMutation();
  const startManualParticipantPropertyLotteryMutation = useStartManualParticipantPropertyLotteryMutation();

  // Manual input state for siraNo (participant)
  const [siraNo, setSiraNo] = useState("");

  // Manual input state for property (only for lotteryKind 2)
  const [propertySiraNo, setPropertySiraNo] = useState("");

  // Focus states for counter styling
  const [isCounterFocused, setIsCounterFocused] = useState(false);
  const [isPropertyCounterFocused, setIsPropertyCounterFocused] = useState(false);

  // Winners list
  // const [winners, setWinners] = useState<WinnerData[]>([]);
  const [winners, setWinners] = useState<Record<string, any>[]>([]);

  // Track if we're waiting for a property draw (participant drawn but property not yet drawn)
  const [isWaitingForProperty, setIsWaitingForProperty] = useState(false);

  // Remaining participants count from SignalR (KalanHakSahibi from backend)
  const [kalanHakSahibi, setKalanHakSahibi] = useState<number | null>(null);

  // Status state - API response'undan güncellenir
  const [lotteryStatus, setLotteryStatus] = useState<number | null>(null);

  // İlk yüklemede kuraStartDetail'dan set et
  useEffect(() => {
    if (kuraStartDetail?.status !== undefined && lotteryStatus === null) {
      setLotteryStatus(kuraStartDetail.status);
    }
  }, [kuraStartDetail?.status]); // lotteryStatus dependency'den çıkartın

  const isLotteryStarted = lotteryStatus === KuraStatus.Started;

  // Kura durdurma fonksiyonu
  const handleStopKura = async () => {
    try {
      const response = await api.post(`/lotteries/${kuraId}/stop-draw`);
      if (response.status !== 200) {
        throw new Error("Kura durdurulamadı");
      }
      setLotteryStatus(KuraStatus.Stopped); // veya response'dan gelen status
      toast.success(
        <span className="text-2xl font-extrabold whitespace-nowrap">
          KURA ÇEKİLİŞİ DURDURULDU
        </span>,
        { duration: 5000 }
      );
    } catch (error: any) {
      const errorMessage = handleApiError(error, "Kura durdurulamadı");
      toast.error(errorMessage);
    }
  };

  console.log("kalan haksahibi manuel- ", kalanHakSahibi);
  console.log("kuraStartDetail?.kalanHakSahibi manuel- ", kuraStartDetail?.kalanHakSahibi);
  console.log("kuraStartDetail?.primaryCount manuel- ", kuraStartDetail?.primaryCount);
  console.log("kuraStartDetail?.status - ", kuraStartDetail?.status);

  // Helper function to calculate font size and box size based on digit count
  const getCounterStyle = (value: string) => {
    const digitCount = value.length;
    if (digitCount <= 3) {
      return {
        fontSize: 'text-4xl lg:text-[40px]',
        padding: '26px 24px',
        gap: 'gap-2 xl:gap-5'
      };
    }
    if (digitCount === 4) {
      return {
        fontSize: 'text-4xl lg:text-[38px]',
        padding: '24px 22px',
        gap: 'gap-2 xl:gap-4'
      };
    }
    if (digitCount === 5) {
      return {
        fontSize: 'text-3xl lg:text-[34px]',
        padding: '20px 18px',
        gap: 'gap-2 xl:gap-3.5'
      };
    }
    if (digitCount === 6) {
      return {
        fontSize: 'text-2xl lg:text-[30px]',
        padding: '16px 14px',
        gap: 'gap-1.5 xl:gap-3'
      };
    }
    // 7+ digits
    return {
      fontSize: 'text-xl lg:text-[26px]',
      padding: '14px 12px',
      gap: 'gap-1.5 xl:gap-2.5'
    };
  };

  // Track editing state for each row and column (using composite key: "rowIndex-columnName")
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});

  // Fetch settings using React Query
  const { data: settings } = useSettingsQuery();

  console.log("Winner data ->", winners);

  // Get lottery kind from API data
  const lotteryKind = kuraStartDetail?.lotteryKind || 1;
  // Kind 4 ("Katılımcı + Konut + Diğer + 65 Yaş") canlı çekilişte kind 2 ile aynı davranır;
  // farkı yalnızca konutun hangi katılımcıya gittiğidir.
  const isParticipantPropertyKind = lotteryKind === 2 || lotteryKind === 4;

  // Determine if we need participant+property lottery (lotteryKind 2)
  const isParticipantPropertyLottery = isParticipantPropertyKind;

  // Function to manually draw a winner
  const handleDrawWinner = async (e: FormEvent, drawType: 'participant' | 'property') => {
    e.preventDefault();

    // Blur the input immediately to hide it
    if (drawType === 'participant') {
      document.getElementById('siraNo')?.blur();
    } else {
      document.getElementById('propertySiraNo')?.blur();
    }

    // Validate inputs based on draw type
    if (drawType === 'participant') {
      if (!siraNo || siraNo.trim() === "") {
        toast.error("Lütfen katılımcı sıra no giriniz");
        return;
      }

      // For participant-property lottery, prevent drawing a new participant if already waiting for property
      if (isParticipantPropertyLottery && isWaitingForProperty) {
        toast.error("Önce mevcut katılımcı için konut çekmelisiniz");
        return;
      }
    } else if (drawType === 'property') {
      if (!propertySiraNo || propertySiraNo.trim() === "") {
        toast.error("Lütfen konut sıra no giriniz");
        return;
      }

      // Check if we have a participant waiting for property
      if (!isWaitingForProperty) {
        toast.error("Önce katılımcı çekmelisiniz");
        return;
      }
    }

    try {
      let response;

      if (isParticipantPropertyLottery) {
        // For lotteryKind 2: use participant-property endpoint
        // When drawing participant: lotteryKind = 1
        // When drawing property: lotteryKind = 3
        const apiLotteryKind = drawType === 'participant' ? 1 : 3;
        const inputSiraNo = drawType === 'participant' ? parseInt(siraNo, 10) : parseInt(propertySiraNo, 10);

        // For property draw, we need to send CekilisSiraNo (the last participant's draw number)
        const cekilisSiraNo = drawType === 'property' && winners.length > 0
          ? winners[0].cekilisSiraNo || winners[0].CekilisSiraNo
          : undefined;

        response = await startManualParticipantPropertyLotteryMutation.mutateAsync({
          lotteryId: kuraId,
          siraNo: inputSiraNo,
          lotteryKind: apiLotteryKind,
          cekilisSiraNo: cekilisSiraNo
        });
      } else {
        // For lotteryKind 1 or 3: use regular endpoint
        response = await startManualLotteryMutation.mutateAsync({
          lotteryId: kuraId,
          siraNo: parseInt(siraNo, 10)
        });
      }

      if (response && response.flatUiData) {
        if (drawType === 'participant' && isParticipantPropertyLottery) {
          // Add participant to the list and set waiting state
          setWinners((prev) => [response.flatUiData, ...prev]);
          setIsWaitingForProperty(true);
          toast.success("Katılımcı çekildi - Şimdi konut çekin");
        } else if (drawType === 'property' && isParticipantPropertyLottery) {
          // Update the first row (latest participant) with property data
          setWinners((prev) => {
            const updated = [...prev];
            if (updated.length > 0) {
              // Merge property data into the first row
              updated[0] = { ...updated[0], ...response.flatUiData };
            }
            return updated;
          });
          setIsWaitingForProperty(false);
          toast.success("Konut eşleştirildi");
        } else {
          // Regular lottery (not participant-property)
          setWinners((prev) => [response.flatUiData, ...prev]);
          toast.success("Kazanan çekildi");
        }

        // Update kalanHakSahibi from API response if provided
        if (response.kalanHakSahibi !== undefined && response.kalanHakSahibi !== null) {
          setKalanHakSahibi(response.kalanHakSahibi);
        } else if (drawType === 'participant' && !isParticipantPropertyLottery) {
          // Fallback: local decrement if API doesn't return the value
          setKalanHakSahibi((prev) => (prev !== null ? Math.max(0, prev - 1) : prev));
        }

        if (response.status !== undefined && response.status !== null) {
          setLotteryStatus(response.status);
        }

        // Clear input after successful draw
        if (drawType === 'participant') {
          setSiraNo("");
        } else {
          setPropertySiraNo("");
        }

        if (response.tamamlandiMi === true && response.tamamlandiMi !== undefined && response.tamamlandiMi !== null) {
          toast.success(
            <span className="text-xl font-extrabold whitespace-nowrap">
              KURA ÇEKİLİŞİ TAMAMLANDI
            </span>,
            {
              duration: 5000,
            }
          );
        }


      }
    } catch (error: any) {
      console.error("Error drawing winner:", error);

      const errorMessage = handleApiError(error, "Kazanan çekilemedi");
      toast.error(errorMessage);
    }
  };

  // Function to handle orderNo edit
  const handleOrderNoEdit = async (
    winnerIndex: number,
    newSiraNo: string,
    cekilisSiraNo: number,
    orderNoColumnName: string
  ) => {
    const originalValue = winners[winnerIndex][orderNoColumnName];

    // Do nothing if value hasn't changed
    if (newSiraNo === originalValue?.toString()) {
      // Clear editing state if value is the same
      setEditingValues((prev) => {
        const updated = { ...prev };
        delete updated[`${winnerIndex}-${orderNoColumnName}`];
        return updated;
      });
      return;
    }

    // Validate input
    if (!newSiraNo || newSiraNo.trim() === "") {
      toast.error("Lütfen sıra no giriniz");
      // Revert to original value
      setEditingValues((prev) => {
        const updated = { ...prev };
        delete updated[`${winnerIndex}-${orderNoColumnName}`];
        return updated;
      });
      return;
    }

    try {
      let response;

      if (isParticipantPropertyLottery) {
        // For lotteryKind 2: determine if editing participant (1) or property (3)
        // Check if the column is for participant or property based on settings
        const column = allColumns.find(col => col.columnName === orderNoColumnName);
        const isPropertyColumn = column && column.totalProperties !== null && column.totalProperties !== undefined;
        const apiLotteryKind = isPropertyColumn ? 3 : 1;

        response = await startManualParticipantPropertyLotteryMutation.mutateAsync({
          lotteryId: kuraId,
          siraNo: parseInt(newSiraNo, 10),
          lotteryKind: apiLotteryKind,
          cekilisSiraNo: cekilisSiraNo,
          isEdit: true
        });
      } else {
        // For lotteryKind 1 or 3: use regular endpoint
        response = await startManualLotteryMutation.mutateAsync({
          lotteryId: kuraId,
          siraNo: parseInt(newSiraNo, 10),
          cekilisSiraNo: cekilisSiraNo,
          isEdit: true
        });
      }

      if (response && response.flatUiData) {
        // Update only the changed fields, not the entire row
        setWinners((prev) => {
          const updated = [...prev];
          // Merge the response data with existing row data to preserve unchanged fields
          updated[winnerIndex] = {
            ...updated[winnerIndex],
            ...response.flatUiData
          };
          return updated;
        });

        // If we just added property data for the first row (winnerIndex 0) while waiting for property,
        // reset the waiting state to allow drawing new participants
        if (isParticipantPropertyLottery && isWaitingForProperty && winnerIndex === 0) {
          const column = allColumns.find(col => col.columnName === orderNoColumnName);
          const isPropertyColumn = column && column.totalProperties !== null && column.totalProperties !== undefined;

          // If we just edited a property column, we're done waiting
          if (isPropertyColumn) {
            setIsWaitingForProperty(false);
          }
        }

        // Clear editing state after successful update
        setEditingValues((prev) => {
          const updated = { ...prev };
          delete updated[`${winnerIndex}-${orderNoColumnName}`];
          return updated;
        });

        toast.success("Kazanan güncellendi");
      }
    } catch (error: any) {
      console.error("Error updating winner:", error);

      const errorMessage = handleApiError(error, "Kazanan güncellenemedi");
      toast.error(errorMessage);

      // Revert to original value on error
      setEditingValues((prev) => {
        const updated = { ...prev };
        delete updated[`${winnerIndex}-${orderNoColumnName}`];
        return updated;
      });
    }
  };

  // Get kura name from API data
  const kuraName = kuraStartDetail?.lotteryName || "";

  // Marquee text from settings API
  const marqueeText = settings?.description || "";

  // Calculate digit counts for counters based on backend-configured max digits
  const participantDigits = Math.max(
    0,
    kuraStartDetail?.maxDigitInSiraNoColumnParticipant ??
    Math.max(2, (kuraStartDetail?.participantTotalCount || 0).toString().length)
  );
  const propertyDigits = Math.max(
    0,
    kuraStartDetail?.maxDigitInSiraNoColumnProperty ??
    Math.max(2, (kuraStartDetail?.propertiesTotalCount || 0).toString().length)
  );

  // Image URL from API - use lotteryImage if available, otherwise use settings byteDosya
  const getImageUrl = () => {
    if (kuraStartDetail?.lotteryImage) {
      // Eğer zaten data:image ile başlamıyorsa prefix ekle
      if (!kuraStartDetail.lotteryImage.startsWith("data:image")) {
        return `data:image/png;base64,${kuraStartDetail.lotteryImage}`;
      }
      return kuraStartDetail.lotteryImage;
    }

    if (settings?.byteDosya) {
      return `data:image/png;base64,${settings.byteDosya}`;
    }

    return "";
  };
  const kuraImageUrl = getImageUrl();

  // Background image URL from settings
  const getBackgroundImageUrl = () => {
    if (settings?.byteBackgroundDosya) {
      return `data:image/png;base64,${settings.byteBackgroundDosya}`;
    }
    return "";
  };
  const backgroundImageUrl = getBackgroundImageUrl();

  // Get all column labels from API (including both participant and property columns)
  // Backend only returns visible columns in order
  const allColumns = kuraStartDetail?.getLotterySettingsList || [];

  // Initialize winners from API data and check if waiting for property
  useEffect(() => {
    const list = kuraStartDetail?.resultList ?? [];

    if (list.length > 0 && list.length > winners.length) {
      setWinners(list);

      // Check if we need to set waiting for property state
      // If the latest winner doesn't have property data, we're waiting for property
      if (isParticipantPropertyLottery && list.length > 0) {
        const latestWinner = list[0];

        // Find participant and property columns
        const participantColumns = allColumns.filter(col =>
          col.totalParticipants !== null && col.totalParticipants !== undefined &&
          col.isOrderNo // Participant order no column (e.g., Participant_A1)
        );
        const propertyColumns = allColumns.filter(col =>
          col.totalProperties !== null && col.totalProperties !== undefined &&
          col.isOrderNo // Property order no column (e.g., Property_A1)
        );

        // Check if participant data exists (participant order no has value)
        const hasParticipantData = participantColumns.some(col => {
          if (col.columnName) {
            const value = latestWinner[col.columnName];
            return value !== null && value !== undefined && value !== "";
          }
          return false;
        });

        // Check if property data exists (property order no has value)
        const hasPropertyData = propertyColumns.some(col => {
          if (col.columnName) {
            const value = latestWinner[col.columnName];
            return value !== null && value !== undefined && value !== "";
          }
          return false;
        });

        // If we have participant but no property, we're waiting for property draw
        if (hasParticipantData && !hasPropertyData) {
          setIsWaitingForProperty(true);
        } else {
          setIsWaitingForProperty(false);
        }

        if (kalanHakSahibi === null) {
          // If backend provides KalanHakSahibi in initial response, use it
          // Otherwise, use total participant count as initial value

          const initialValue = kuraStartDetail?.kalanHakSahibi ?? kuraStartDetail?.primaryCount ?? 0;
          setKalanHakSahibi(initialValue);
        }

        if (kuraStartDetail?.status !== undefined && lotteryStatus === null) {
          setLotteryStatus(kuraStartDetail.status);
        }

      }
    }
  }, [kuraStartDetail, isParticipantPropertyLottery, allColumns, winners.length, kalanHakSahibi, lotteryStatus]);

  // Table columns configuration from API
  // Map columns with their columnName to match with winner data
  const tableColumns = allColumns.map((setting, index) => ({
    key: `${setting.label}_${index}`, // Use label + index to ensure unique keys
    label: setting.label,
    columnName: setting.columnName, // Store columnName to match winner data
    index: index, // Store index for fallback
    isOrderNo: setting.isOrderNo, // Flag to identify orderNo columns
  }));
  console.log(tableColumns);
  // Calculate visible columns count for colspan
  const visibleColumnsCount = tableColumns.length;

  // Show loading state while fetching data
  if (isDetailLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto p-8">
          <div className="space-y-6">
            <div className="container mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Image skeleton */}
              <div className="lg:col-span-8 xl:col-span-6 lg:order-2">
                <Skeleton className="w-full h-[400px] rounded-lg" />
              </div>
              {/* Stats skeleton */}
              <div className="lg:col-span-4 xl:col-span-3 lg:order-1 bg-card border rounded-lg shadow-sm">
                <div className="p-6 space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if data fetch failed
  if (detailError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold text-red-600">
            Veri yüklenirken hata oluştu
          </h2>
          <p className="text-muted-foreground">
            {detailError?.message || "Bilinmeyen hata"}
          </p>
          <Button onClick={() => window.location.reload()}>Tekrar Dene</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background isolate relative font-[Inter] kura-start-page">
      {/* Fullscreen kura start page without header/sidebar */}
      <div className="mx-auto min-h-screen flex flex-col">
        <div className="space-y-6 lg:space-y-4 mb-8">
          {/* First Row: 3 columns - Stats list in first column (smaller), second column larger */}
          <div className="container-fluid mx-auto grid grid-cols-1 lg:grid-cols-12 items-start px-4 xl:px-6 pt-6">
            {/* First Column: Stats List (shows second on mobile/tablet) */}
            <div
              className="lg:sticky lg:top-0 lg:col-span-3 order-2 lg:order-1 flex flex-col rounded-xl"
              style={{
                border: "1px solid #D4D4D4",
                boxShadow: "0px 4px 11px 5px #7272721A",
              }}
            >
              {/* Stats Section */}
              <div className="px-4 py-4 space-y-4 rounded-xl">
                {/* Row 1: Toplam Başvuru */}
                <div
                  className="p-4 rounded-lg border-muted mb-4 relative isolate bg-[#fff]"
                  style={{
                    border: "2px solid #374151",
                    boxShadow: "0px 7px 20px 9px #7272720A",
                  }}
                >
                  <span className="text-base font-semibold leading-none text-[#181818]">
                    Toplam Başvuru
                  </span>
                  <span className="text-[40px] font-semibold leading-none block mt-2 text-[#252525]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {isDetailLoading
                      ? "..."
                      : formatNumberWithDots(kuraStartDetail?.participantTotalCount)}
                  </span>
                </div>


                <div className="grid grid-cols-2 gap-4">
                  {/* Toplam Hak Sahibi Box */}
                  <div
                    className="text-center p-4 rounded-lg border-muted relative isolate bg-[#fff]"
                    style={{
                      border: "2px solid #374151",
                      boxShadow: "0px 7px 20px 9px #7272720A",
                    }}
                  >
                    <span
                      className={`${getResponsiveFontSize(kuraStartDetail?.primaryCount, 32)} font-semibold leading-none block mb-4 text-[#252525] text-center`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {isDetailLoading
                        ? "..."
                        : formatNumberWithDots(kuraStartDetail?.primaryCount)}
                    </span>
                    <span className="text-base font-semibold leading-none text-[#181818]">
                      Toplam Hak Sahibi
                    </span>
                  </div>

                  {/* Kalan Hak Sahibi Box */}
                  <div
                    className="text-center p-4 rounded-lg border-muted relative isolate bg-[#fff]"
                    style={{
                      border: "2px solid #374151",
                      boxShadow: "0px 7px 20px 9px #7272720A",
                    }}
                  >
                    <span
                      className={`${getResponsiveFontSize(kalanHakSahibi !== null ? kalanHakSahibi : kuraStartDetail?.kalanHakSahibi || 0, 32)} font-semibold leading-none block mb-4 text-[#252525] text-center`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {isDetailLoading
                        ? "..."
                        : formatNumberWithDots(kalanHakSahibi ?? kuraStartDetail?.kalanHakSahibi)}
                    </span>
                    <span className="text-base font-semibold leading-none text-[#181818]">
                      Kalan Hak Sahibi
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-9 lg:order-2 h-full">
              <div className="column-wrapper isolate h-full relative grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Second Column - Kura Image (shows first on mobile/tablet) */}
                <div className="lg:col-span-8 lg:order-1 h-full flex flex-col justify-center py-2 px-4">
                  {/* Image Row */}
                  <div className="flex items-center justify-center w-full mb-6">
                    {isDetailLoading || !kuraImageUrl ? (
                      <Skeleton className="w-full h-[130px] rounded-lg" />
                    ) : (
                      <img
                        src={kuraImageUrl}
                        alt="Kura Görseli"
                        className="w-full h-full object-contain max-w-full lg:max-w-[80%] xl:max-w-[70%] rounded-lg"
                      />
                    )}
                  </div>
                  {/* Kura Name Row */}
                  <div className="container mx-auto text-center mt-auto mb-6">
                    <h2 className="text-2xl lg:text-5xl font-bold leading-none text-[#FFFFFF]">
                      {kuraName}
                    </h2>
                  </div>
                </div>
                {/* Third Column - Stop Button */}
                <div className="hidden lg:flex lg:col-span-4 lg:order-2 justify-end items-start pt-2">
                  {isLotteryStarted && (
                    <Button
                      size="icon"
                      className="rounded-full w-12 h-12 shadow-lg bg-red-600 hover:bg-red-700 text-white"
                      aria-label="Kurayı Durdur"
                      title="Kurayı Durdur"
                      onClick={handleStopKura}
                      disabled={startManualLotteryMutation.isPending || startManualParticipantPropertyLotteryMutation.isPending}
                    >
                      <Square className="h-6 w-6" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Second Row: Manual Input and Winners Table */}
          <div className="space-y-6 mt-8 lg:mt-0">
            {/* Manual Input Form */}
            <div className="container mx-auto mb-10">
              <div className="px-4">
                {/* Grid layout: stacked on mobile, side by side on desktop */}
                <div className={`grid ${isParticipantPropertyLottery ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-8 lg:gap-12`}>
                  {/* Participant Input Section */}
                  <div className="flex flex-col items-center gap-6">
                    <div className="w-full">
                      <label
                        htmlFor="siraNo"
                        className="block text-base font-semibold mb-4 text-center text-[#FFFFFF]"
                      >
                        {isParticipantPropertyLottery ? "Katılımcı Sıra No" : "Sıra No"}
                      </label>
                      <div className="relative">
                        {/* Hidden input for form submission and keyboard input */}
                        <Input
                          id="siraNo"
                          type="number"
                          value={siraNo}
                          onChange={(e) => setSiraNo(e.target.value)}
                          onFocus={() => setIsCounterFocused(true)}
                          onBlur={() => setIsCounterFocused(false)}
                          onWheel={(e) => e.currentTarget.blur()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && siraNo && !startManualLotteryMutation.isPending && !startManualParticipantPropertyLotteryMutation.isPending) {
                              handleDrawWinner(e as any, 'participant');
                            }
                          }}
                          placeholder="0"
                          className="!opacity-0 absolute inset-0 w-full h-full !pointer-events-none z-10"
                          disabled={startManualLotteryMutation.isPending || startManualParticipantPropertyLotteryMutation.isPending || (isParticipantPropertyLottery && isWaitingForProperty)}
                          min="1"
                          autoComplete="off"
                        />
                        {/* Slot-style Visual Display - Each digit in its own slot */}
                        <div
                          className={`flex justify-center items-center ${getCounterStyle(siraNo || '0').gap} ${(isParticipantPropertyLottery && isWaitingForProperty) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} transition-all duration-200`}
                          onClick={() => {
                            if (!(isParticipantPropertyLottery && isWaitingForProperty)) {
                              document.getElementById('siraNo')?.focus();
                            }
                          }}
                        >
                          {(siraNo || '0').padStart(participantDigits, "0").split("").map((digit, index) => (
                            <div
                              key={index}
                              className="slot-counter-char flex items-center justify-center transition-all duration-200"
                              style={{
                                transform: isCounterFocused ? 'scale(1.05)' : 'scale(1)',
                                boxShadow: isCounterFocused
                                  ? '0px 4px 11px 5px #5367B0'
                                  : '0px 4px 11px 5px #4C60A99E',
                                borderColor: isCounterFocused ? '#5367B0' : '#eaeaea',
                                borderWidth: '2px',
                              }}
                            >
                              <SlidingNumber
                                number={parseInt(digit)}
                                className={`slot-counter-number ${getCounterStyle(siraNo || '0').fontSize}`}
                                style={{
                                  padding: getCounterStyle(siraNo || '0').padding
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Participant Draw Button */}
                    <Button
                      type="button"
                      onClick={(e) => handleDrawWinner(e as any, 'participant')}
                      disabled={startManualLotteryMutation.isPending || startManualParticipantPropertyLotteryMutation.isPending || !siraNo || (isParticipantPropertyLottery && isWaitingForProperty)}
                      className="w-full sm:w-auto text-white text-base font-semibold px-7 py-2.5 rounded-3xl cursor-pointer disabled:opacity-50"
                      style={{
                        background: "linear-gradient(180deg, #5165AE 0%, #3D519A 100%)",
                        boxShadow: "0px 1px 7.9px 0px #FFFFFF3B",
                      }}
                    >
                      {(startManualLotteryMutation.isPending || startManualParticipantPropertyLotteryMutation.isPending) ? "Çekiliyor..." : "Katılımcı Çek"}
                    </Button>
                  </div>

                  {/* Property Input Section - Only show for lotteryKind 2 */}
                  {isParticipantPropertyLottery && (
                    <div className="flex flex-col items-center gap-6">
                      <div className="w-full">
                        <label
                          htmlFor="propertySiraNo"
                          className="block text-base font-semibold mb-4 text-center text-[#FFFFFF]"
                        >
                          Konut Sıra No
                        </label>
                        <div className="relative">
                          {/* Hidden input for form submission and keyboard input */}
                          <Input
                            id="propertySiraNo"
                            type="number"
                            value={propertySiraNo}
                            onChange={(e) => setPropertySiraNo(e.target.value)}
                            onFocus={() => setIsPropertyCounterFocused(true)}
                            onBlur={() => setIsPropertyCounterFocused(false)}
                            onWheel={(e) => e.currentTarget.blur()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && propertySiraNo && !startManualParticipantPropertyLotteryMutation.isPending && isWaitingForProperty) {
                                handleDrawWinner(e as any, 'property');
                              }
                            }}
                            placeholder="0"
                            className="!opacity-0 absolute inset-0 w-full h-full !pointer-events-none z-10"
                            disabled={startManualParticipantPropertyLotteryMutation.isPending || !isWaitingForProperty}
                            min="1"
                            autoComplete="off"
                          />
                          {/* Slot-style Visual Display - Each digit in its own slot */}
                          <div
                            className={`flex justify-center items-center ${getCounterStyle(propertySiraNo || '0').gap} ${isWaitingForProperty ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} transition-all duration-200`}
                            onClick={() => {
                              if (isWaitingForProperty) {
                                document.getElementById('propertySiraNo')?.focus();
                              }
                            }}
                          >
                            {(propertySiraNo || '0').padStart(propertyDigits, "0").split("").map((digit, index) => (
                              <div
                                key={index}
                                className="slot-counter-char flex items-center justify-center transition-all duration-200"
                                style={{
                                  transform: isPropertyCounterFocused ? 'scale(1.05)' : 'scale(1)',
                                  boxShadow: isPropertyCounterFocused
                                    ? '0px 4px 11px 5px #5367B0'
                                    : '0px 4px 11px 5px #4C60A99E',
                                  borderColor: isPropertyCounterFocused ? '#5367B0' : '#eaeaea',
                                  borderWidth: '2px',
                                }}
                              >
                                <SlidingNumber
                                  number={parseInt(digit)}
                                  className={`slot-counter-number ${getCounterStyle(propertySiraNo || '0').fontSize}`}
                                  style={{
                                    padding: getCounterStyle(propertySiraNo || '0').padding
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Property Draw Button */}
                      <Button
                        type="button"
                        onClick={(e) => handleDrawWinner(e as any, 'property')}
                        disabled={startManualParticipantPropertyLotteryMutation.isPending || !propertySiraNo || !isWaitingForProperty}
                        className="w-full sm:w-auto text-white text-base font-semibold px-7 py-2.5 rounded-3xl cursor-pointer disabled:opacity-50"
                        style={{
                          background: "linear-gradient(180deg, #AE5151 0%, #9A3D3D 100%)",
                          boxShadow: "0px 1px 7.9px 0px #FFFFFF3B",
                        }}
                      >
                        {startManualParticipantPropertyLotteryMutation.isPending ? "Çekiliyor..." : (isWaitingForProperty ? "Konut Çek" : "Önce Katılımcı Çek")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Winners Table */}
            <div
              className="bg-card overflow-hidden"
              style={{
                border: "1px solid #D9D9D9",
                boxShadow: "0px 4px 11px 5px #7272721A",
                borderRadius: "12px",
              }}
            >
              <div className="overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      {tableColumns.map((column) => (
                        <TableHead
                          key={column.key}
                          className="whitespace-nowrap py-9 px-4 text-xl font-extrabold leading-none text-[#363636]"
                        >
                          {column.label}

                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {winners.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={visibleColumnsCount}
                          className="p-0"
                        >
                          <div className="flex items-center justify-center py-20 pt-6 px-6 md:p-12 md:pb-20 md:pt-6 sticky left-0 w-full max-w-[calc(100vw-4rem)]">
                            <div className="max-w-full whitespace-normal">
                              <p className="text-sm text-center text-foreground font-semibold">
                                Kazananlar henüz belirlenmedi.
                              </p>
                              <p className="text-sm text-primary/50 text-center font-semibold">
                                Sıra no girerek kazanan çekin.
                              </p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      winners.map((winner, winnerIndex) => {
                        // Get the cekilisSiraNo for this row
                        const cekilisSiraNo = winner.CekilisSiraNo || winner.cekilisSiraNo;

                        return (
                          <TableRow
                            key={winnerIndex}
                            style={{
                              background:
                                winnerIndex === 0
                                  ? "linear-gradient(111.39deg, #5367B0 15.9%, #394D96 89.86%)"
                                  : winnerIndex % 2 === 1
                                    ? "#FFFFFF"
                                    : "rgba(82, 102, 175, 0.2)",
                            }}
                          >
                            {tableColumns.map((column) => {
                              // Match winner data using columnName from settings
                              const value = column.columnName ? winner[column.columnName] : null;

                              const isDurumColumn = column.label.toLowerCase().includes("durum");
                              const isOrderNo = column.isOrderNo;

                              const primaryTitle = kuraStartDetail?.primaryTitle
                                ?.toString()
                                .toLowerCase();
                              const backupTitle = kuraStartDetail?.backupTitle
                                ?.toString()
                                .toLowerCase();
                              const currentValue = value?.toString().toLowerCase();

                              // Get current editing value or default to the actual value (using composite key)
                              const editKey = `${winnerIndex}-${column.columnName}`;
                              const currentEditValue = editingValues[editKey] ?? value?.toString() ?? "";

                              return (
                                <TableCell
                                  key={column.key}
                                  className="text-[22px] font-semibold leading-none py-6 px-4"
                                  style={{
                                    color: winnerIndex === 0 ? "#fff" : "#101010",
                                  }}
                                >
                                  {isOrderNo ? (
                                    // Render input for orderNo columns
                                    <Input
                                      type="number"
                                      value={currentEditValue}
                                      onChange={(e) => {
                                        setEditingValues((prev) => ({
                                          ...prev,
                                          [editKey]: e.target.value,
                                        }));
                                      }}
                                      onBlur={() => {
                                        // Handle edit on blur
                                        if (cekilisSiraNo && column.columnName) {
                                          handleOrderNoEdit(
                                            winnerIndex,
                                            currentEditValue,
                                            cekilisSiraNo,
                                            column.columnName
                                          );
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          // Just blur the input - onBlur will handle the request
                                          e.currentTarget.blur();
                                        } else if (e.key === "Escape") {
                                          // Revert on Escape key
                                          setEditingValues((prev) => {
                                            const updated = { ...prev };
                                            delete updated[editKey];
                                            return updated;
                                          });
                                          e.currentTarget.blur();
                                        }
                                      }}
                                      className="w-24 border-2 border-gray-400 focus:border-blue-500 text-gray-900"
                                      style={{
                                        backgroundColor: winnerIndex === 0 ? 'rgba(255, 255, 255, 0.9)' : '#ffffff',
                                        color: '#111827'
                                      }}
                                      min="1"
                                    />
                                  ) : isDurumColumn && value ? (
                                    <span
                                      className={`px-4 py-1 rounded-full text-[19px] font-semibold ${currentValue === primaryTitle
                                        ? "bg-[#10B98133] text-[#10B981] border border-[#10B9814D]"
                                        : currentValue === backupTitle
                                          ? "bg-[#FDE68A33] text-[#D97706] border border-[#D977064D]"
                                          : "bg-gray-100 text-gray-800"
                                        }`}
                                    >
                                      {value}
                                    </span>
                                  ) : (
                                    (value ?? "-")
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>

                </Table>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Footer with Marquee and FAB */}
        <div className="sticky bottom-4 z-50 mt-auto mx-4">
          <div className="flex items-center gap-4">
            {/* Marquee Section - flex-grow to take available space but with min-width 0 to allow shrinking */}
            <div
              className="flex-grow min-w-0 rounded-lg py-3 bg-[#5367B0]"
              style={{
                boxShadow: "0px 4px 11px 3px #7272721A",
              }}
            >
              <Marquee speed={50} gradient={false}>
                <span className="text-sm font-semibold leading-5 text-white mx-8">
                  {marqueeText}
                </span>
              </Marquee>
            </div>

            {/* Button Section - fixed width to prevent being pushed out */}
            <div className="flex-shrink-0 flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    size="icon"
                    className="rounded-full w-12 h-12 shadow-lg bg-[#5367B0] hover:bg-[#394D96]"
                    aria-label="Menu"
                  >
                    <Menu className="h-7 w-7" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="end"
                  sideOffset={10}
                  className="w-auto p-2"
                >
                  <div className="flex flex-col gap-2">
                    {/* Homepage Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link to="/" className="flex items-center gap-2">
                        <Home className="h-4 w-4" />
                        Ana Sayfa
                      </Link>
                    </Button>

                    {/* Kura List Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link
                        to="/kura"
                        search={{ search: "" }}
                        className="flex items-center gap-2"
                      >
                        <List className="h-4 w-4" />
                        Kura Listesi
                      </Link>
                    </Button>

                    {/* Kura Detail Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link
                        to="/kura/$kuraId/edit"
                        params={{ kuraId }}
                        className="flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Kura Detay
                      </Link>
                    </Button>

                    {/* Winners List Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link
                        to="/kura/$kuraId/winners"
                        params={{ kuraId }}
                        className="flex items-center gap-2"
                      >
                        <Trophy className="h-4 w-4" />
                        Kazanan Listesi
                      </Link>
                    </Button>

                    {/* Start Kura Button */}
                    {isLotteryStarted && (
                      <Button
                        variant="ghost"
                        className="justify-start text-red-600 hover:text-red-700"
                        onClick={handleStopKura}
                        disabled={startManualLotteryMutation.isPending || startManualParticipantPropertyLotteryMutation.isPending}
                      >
                        <Square className="h-4 w-4 mr-2" />
                        Kurayı Durdur
                      </Button>
                    )}

                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>
      <div
        className="fixed top-0 left-0 -z-10 h-screen w-screen"
        style={{
          background:
            "linear-gradient(180.23deg, rgba(22, 26, 73, 0.41) 4.54%, #1D2052 94.97%)",
        }}
      >
        {backgroundImageUrl ? (
          <img
            src={backgroundImageUrl}
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
        ) : (
          <img
            src="../../lottery-start-bg.jpeg"
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
        )}
      </div>

      {/* Loading Modal */}
      <LoadingModal
        isOpen={startManualLotteryMutation.isPending}
        message="Lütfen Bekleyin..."
      />
    </div>
  );
}
